import { useState, useMemo, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Body1,
  Caption1,
  Input,
  Dropdown,
  Option,
  Tooltip,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { PageTitle } from '@/components/PageTitle';
import {
  Search24Regular,
  Checkmark24Regular,
} from '@fluentui/react-icons';
import { useSharePointList } from '@/hooks/useSharePointList';
import type {
  Employee,
  Application,
  AppAssignment,
  SPListItem,
} from '@/models/types';
import { useAppContext } from '@/store/appContext';
import { canManageAccess, canViewAllMembers } from '@/utils/permissions';
import { AccessDenied } from '@/components/AccessDenied';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '20px' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  toolbar: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  },
  matrixOuter: {
    position: 'relative' as const,
    '@media (max-width: 480px)': {
      marginLeft: '-12px',
      marginRight: '-12px',
    },
  },
  matrixWrapper: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow4,
    overflow: 'auto',
    maxHeight: 'calc(100vh - 240px)',
    WebkitOverflowScrolling: 'touch' as const,
  },
  scrollHint: {
    position: 'absolute' as const,
    top: 0,
    right: 0,
    bottom: 0,
    width: '24px',
    pointerEvents: 'none' as const,
    background: `linear-gradient(to right, transparent, ${tokens.colorNeutralBackground1})`,
    borderTopRightRadius: tokens.borderRadiusLarge,
    borderBottomRightRadius: tokens.borderRadiusLarge,
    '@media (min-width: 900px)': {
      display: 'none',
    },
  },
  scrollNote: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'block',
      padding: '6px 12px',
      fontSize: '11px',
      color: tokens.colorNeutralForeground3,
      textAlign: 'center' as const,
    },
  },
  table: {
    borderCollapse: 'collapse' as const,
    width: 'max-content',
    minWidth: '100%',
  },
  stickyHeader: {
    position: 'sticky' as const,
    top: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    zIndex: 2,
  },
  stickyCol: {
    position: 'sticky' as const,
    left: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    zIndex: 1,
  },
  th: {
    padding: '10px 12px',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
    whiteSpace: 'nowrap' as const,
    maxWidth: '120px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  thApp: {
    writingMode: 'vertical-lr' as const,
    transform: 'rotate(180deg)',
    height: '140px',
    verticalAlign: 'bottom',
    padding: '8px 6px',
    fontSize: '11px',
    cursor: 'default',
  },
  td: {
    padding: '4px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    textAlign: 'center' as const,
  },
  tdName: {
    padding: '8px 14px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    whiteSpace: 'nowrap' as const,
    minWidth: '200px',
  },
  cell: {
    width: '36px',
    height: '36px',
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    margin: '0 auto',
    transition: 'all 0.15s',
  },
  cellAssigned: {
    backgroundColor: '#107c10',
    color: 'white',
    '&:hover': {
      backgroundColor: '#0b5a08',
    },
  },
  cellEmpty: {
    backgroundColor: tokens.colorNeutralBackground3,
    '&:hover': {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  legend: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    padding: '12px 16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  legendDot: {
    width: '12px',
    height: '12px',
    borderRadius: '2px',
  },
});

export function AccessMatrix() {
  const styles = useStyles();
  const { currentUser, listIds } = useAppContext();
  const canEdit = currentUser ? canManageAccess(currentUser.role) : false;

  // Security: Access matrix shows org-wide data — Admin/RO only
  if (currentUser && !canViewAllMembers(currentUser.role)) {
    return <AccessDenied reason="Access matrix is restricted to Admin and Reporting Officers." />;
  }
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [updating, setUpdating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { items: employees } = useSharePointList<Employee>('OC_Employees', {
    autoFetch: !!listIds,
  });
  const { items: apps } = useSharePointList<Application>('OC_Applications', {
    autoFetch: !!listIds,
  });
  const {
    items: assignments,
    create: createAssignment,
    update: updateAssignment,
  } = useSharePointList<AppAssignment>('OC_AppAssignments', {
    autoFetch: !!listIds,
  });

  const departments = useMemo(() => {
    const depts = new Set<string>();
    for (const e of employees) {
      if (e.fields.Department) depts.add(e.fields.Department);
    }
    return Array.from(depts).sort();
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    let result = employees.filter((e) => e.fields.Status === 'Active');

    if (deptFilter !== 'all') {
      result = result.filter((e) => e.fields.Department === deptFilter);
    }

    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        (e) =>
          e.fields.Title.toLowerCase().includes(lower) ||
          e.fields.Email.toLowerCase().includes(lower),
      );
    }

    return result;
  }, [employees, deptFilter, search]);

  const assignmentMap = useMemo(() => {
    const map = new Map<string, SPListItem<AppAssignment>>();
    for (const a of assignments) {
      if (a.fields.Status === 'Active') {
        map.set(`${a.fields.EmployeeId}:${a.fields.AppId}`, a);
      }
    }
    return map;
  }, [assignments]);

  const isAssigned = (empId: string, appId: string) =>
    assignmentMap.has(`${empId}:${appId}`);

  const handleCellClick = useCallback(
    async (emp: SPListItem<Employee>, app: SPListItem<Application>) => {
      if (!canEdit) return;

      const key = `${emp.id}:${app.id}`;
      setUpdating(key);
      setError(null);

      try {
        const existing = assignmentMap.get(key);
        if (existing) {
          await updateAssignment(existing.id, { Status: 'Revoked' });
        } else {
          await createAssignment({
            EmployeeId: Number(emp.id),
            AppId: Number(app.id),
            LicenseType: app.fields.LicenseModel,
            AssignedDate: new Date().toISOString(),
            Status: 'Active',
            AssignedBy: currentUser?.email ?? '',
          });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update');
      } finally {
        setUpdating(null);
      }
    },
    [canEdit, assignmentMap, updateAssignment, createAssignment, currentUser],
  );

  const sortedApps = useMemo(
    () => [...apps].sort((a, b) => a.fields.Title.localeCompare(b.fields.Title)),
    [apps],
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <PageTitle>Access Matrix</PageTitle>
        <Caption1>
          {filteredEmployees.length} members x {apps.length} apps
        </Caption1>
      </div>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.toolbar}>
        <Input
          contentBefore={<Search24Regular />}
          placeholder="Search members..."
          value={search}
          onChange={(_, d) => setSearch(d.value)}
          style={{ minWidth: 200 }}
        />
        <Dropdown
          placeholder="Department"
          value={deptFilter === 'all' ? 'All Departments' : deptFilter}
          onOptionSelect={(_, d) =>
            setDeptFilter(d.optionValue as string)
          }
          style={{ minWidth: 180 }}
        >
          <Option value="all">All Departments</Option>
          {departments.map((d) => (
            <Option key={d} value={d}>
              {d}
            </Option>
          ))}
        </Dropdown>
      </div>

      {filteredEmployees.length === 0 || apps.length === 0 ? (
        <div
          style={{
            padding: 48,
            textAlign: 'center',
            backgroundColor: tokens.colorNeutralBackground1,
            borderRadius: tokens.borderRadiusLarge,
          }}
        >
          <Body1>
            {employees.length === 0
              ? 'No members found. Sync from Entra ID first.'
              : apps.length === 0
                ? 'No applications registered. Add apps first.'
                : 'No members match your filter.'}
          </Body1>
        </div>
      ) : (
        <div>
          <Caption1 className={styles.scrollNote}>
            Swipe horizontally to see all apps →
          </Caption1>
          <div className={styles.matrixOuter}>
            <div className={styles.matrixWrapper}>
              <table className={styles.table}>
              <thead>
              <tr className={styles.stickyHeader}>
                <th
                  className={`${styles.th} ${styles.stickyCol}`}
                  style={{ zIndex: 3 }}
                >
                  Employee
                </th>
                {sortedApps.map((app) => (
                  <Tooltip
                    key={app.id}
                    content={`${app.fields.Title} (${app.fields.Vendor})`}
                    relationship="label"
                  >
                    <th className={`${styles.th} ${styles.thApp}`}>
                      {app.fields.Title}
                    </th>
                  </Tooltip>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((emp) => (
                <tr key={emp.id}>
                  <td className={`${styles.tdName} ${styles.stickyCol}`}>
                    <Body1 style={{ fontWeight: 500 }}>
                      {emp.fields.Title}
                    </Body1>
                    <Caption1>{emp.fields.Department}</Caption1>
                  </td>
                  {sortedApps.map((app) => {
                    const assigned = isAssigned(emp.id, app.id);
                    const cellKey = `${emp.id}:${app.id}`;
                    const isUpdating = updating === cellKey;

                    return (
                      <td key={app.id} className={styles.td}>
                        <Tooltip
                          content={
                            assigned
                              ? `${emp.fields.Title} has ${app.fields.Title}`
                              : `Assign ${app.fields.Title} to ${emp.fields.Title}`
                          }
                          relationship="description"
                        >
                          <div
                            className={`${styles.cell} ${assigned ? styles.cellAssigned : styles.cellEmpty}`}
                            onClick={() => handleCellClick(emp, app)}
                            style={{
                              opacity: isUpdating ? 0.5 : 1,
                              pointerEvents: isUpdating || !canEdit ? 'none' : 'auto',
                            }}
                          >
                            {isUpdating ? (
                              <Spinner size="tiny" />
                            ) : assigned ? (
                              <Checkmark24Regular style={{ fontSize: 16 }} />
                            ) : null}
                          </div>
                        </Tooltip>
                      </td>
                    );
                  })}
                </tr>
              ))}
              </tbody>
            </table>
            </div>
            <div className={styles.scrollHint} aria-hidden="true" />
          </div>

          <div className={styles.legend}>
            <div className={styles.legendItem}>
              <div
                className={styles.legendDot}
                style={{ backgroundColor: '#107c10' }}
              />
              <Caption1>Assigned</Caption1>
            </div>
            <div className={styles.legendItem}>
              <div
                className={styles.legendDot}
                style={{
                  backgroundColor: tokens.colorNeutralBackground3,
                  border: `1px solid ${tokens.colorNeutralStroke2}`,
                }}
              />
              <Caption1>Not assigned</Caption1>
            </div>
            {canEdit && (
              <Caption1 style={{ marginLeft: 'auto' }}>
                Click a cell to assign or revoke
              </Caption1>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
